import React, { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import CartElements from './cartElements/CartElements';
import CartElementsTac from "./cartElements/CartElementsTac";
import { setScrollToItineraryEntry, toggleExpand } from "../actions/itineraryActions";
import { formatPortName } from "../utils/cruiseUtils";
import ThemableSvgIcon from "./theme/ThemableSvgIcon";
import { imagePath, themableImagePath } from "../utils/imageUtils";
import { getHubBaseUrl } from "../utils/configUtils";
import {getExcursionsText, getItineraryDayLabelWidthClass} from "../utils/themeUtils";
import { classNames } from "../utils/miscUtils";
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { TAC_TEMPLATE_TYPES } from '../constants/tacConstants';
import { dateStringToUrlString } from '../utils/dateTimeUtils';

function ItineraryButton({ onClick, icon, children }) {
    return (
        <button 
            className="group flex items-center border border-primary-darker text-primary-darker hover:bg-primary-darker hover:text-white rounded-lg px-4 py-2 text-sm uppercase cursor-pointer" 
            onClick={onClick}
        >
            <img src={themableImagePath(icon)} className="w-4 h-4 mr-2 shrink-0 group-hover:brightness-0 group-hover:invert" alt="" />
            <span className="font-semibold">{children}</span>
        </button>
    );
}

function ItineraryEntry(props) {
    const scrollRef = useRef();
    const navigate = useNavigate();

    const tacConfig = useSelector(state => state.tac.config) || {};
    const availableRestaurants = useSelector(state => state.tac.restaurantsAvailabilityByDate?.[props.date]) || [];
    const availableActivities = useSelector(state => state.tac.activitiesAvailabilityByDate?.[props.date]) || [];

    const isRestaurantsEnabled = tacConfig.isRestaurantsEnabled;
    const isActivitiesEnabled = tacConfig.isActivitiesEnabled;

    const {t} = useLingui();

    useEffect(() => {
        if (props.scrollTo) {
            window.scrollTo(0, scrollRef.current.offsetTop - 100);
            setScrollToItineraryEntry(null);
        }
    }, [props.scrollTo]);

    const dayHasContent = () => {
        return (isRestaurantsEnabled && availableRestaurants.length > 0) ||
            isActivitiesEnabled && availableActivities.length > 0 ||
            props.splInformationUrl ||
            !(props.firstDay || props.type === "Sea" || props.numberOfBookableExcursions === 0);
    };

    const getExcursionsLink = () => {
        const firstOrLastDay = (props.firstDay || props.lastDay);

        if (firstOrLastDay || props.type === "Sea" || props.numberOfBookableExcursions === 0) {
            return null;
        }

        return (
            <ItineraryButton 
                icon="icon-experiences.svg"
                onClick={() => navigate(t({id: "/excursions"}) + `/${props.id}`)}
            >
                <Trans id="destination_excursions">Destination {getExcursionsText()}</Trans>
            </ItineraryButton>
        );
    };

    return (
        <div className="my-2" ref={scrollRef}>
            <div className="flex bg-primary-lightest shadow">

                <div className={`flex ${getItineraryDayLabelWidthClass()} shrink-0 bg-primary-lighter items-center justify-center`}>
                    <div>
                        <Trans id="day">DAY</Trans> {props.dayOfCruise}
                    </div>
                </div>

                <div className="p-2 grow relative">
                    <div className="flex justify-between gap-4">
                        <div className="font-bold flex items-center py-1">
                            {props.type === "TenderPort" && <img className="inline-block mr-1 align-text-top" src={imagePath("icon_anchor.png") } alt="Tender port" />}
                            {formatPortName(props.type, props.port)}
                        </div>
                        {props.nrItems > 0 &&
                            <div className="flex items-center shrink-0">
                                {!props.expanded && (
                                    <div className="hidden sm:block mr-4">
                                        <Plural id="x_items_added" value={props.nrItems} one="# item added" other="# items added" />
                                    </div>
                                )}
                                <button className="focus:outline-hidden cursor-pointer" onClick={() => toggleExpand(props.id)}>
                                    <ThemableSvgIcon alt="chevron"
                                        className="w-8 h-8"
                                        name={props.expanded ? 'up' : 'down'} />
                                </button>
                            </div>
                        }
                    </div>

                    <div className={classNames(dayHasContent() ? "mt-1" : "", "flex flex-wrap gap-2 md:gap-4") }>

                        {getExcursionsLink()}

                        {!props.lastDay && isRestaurantsEnabled && availableRestaurants.length > 0 &&
                            <ItineraryButton 
                                icon="icon-speciality-dining.svg"
                                onClick={() => navigate(`/tac/restaurants/${dateStringToUrlString(props.date)}`)}
                            >
                                <Trans id="speciality_dining">Speciality Dining</Trans>
                            </ItineraryButton>
                        }

                        {!props.lastDay && isActivitiesEnabled && availableActivities.length > 0 &&
                            <ItineraryButton 
                                icon="icon-activities.svg"
                                onClick={() => navigate(`/tac/restaurants/${dateStringToUrlString(props.date)}/${TAC_TEMPLATE_TYPES.ACTIVITY}`)}
                            >
                                <Trans id="activities">Activities</Trans>
                            </ItineraryButton>
                        }

                        {/* TODO - fix the styling for this special information once known how to handle potential overlaps on tablet screens */}
                        {props.splInformationUrl &&
                            <div className="mt-2 sm:mt-0">
                                <a href={`${getHubBaseUrl(false)}${props.splInformationUrl.url}`}
                                    className="underline text-primary"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Trans id="special_information">Special information</Trans>
                                </a>
                            </div>
                        }
                    </div>
                </div>
            </div>

            {props.nrItems > 0 && props.expanded &&
                <div className="bg-primary-lightest-25 shadow p-4">
                    <CartElements
                        itineraryId={props.id}
                        cartType="itinerary"
                    />

                    <CartElements
                        itineraryId={props.id}
                        cartType="booking"
                    />

                    <CartElementsTac
                        cartType="itinerary"
                        date={props.date}
                    />

                    <CartElementsTac
                        cartType="booking"
                        date={props.date}
                    />
                    
                </div>
            }
        </div>
    );
}

export default ItineraryEntry;